import { useMemo, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { AppealKind, AppealKindOptions, BuyOrderStatus } from '@/constants/common'
import { BuyOrder } from '@/types/common'
import { Button } from '@/components/base'
import { RadioListItem } from '@/components/base/RadioListItem'
import BottomActionPanel from '@/components/BottomActionPanel'
import SectionHeader from '@/components/SectionHeader'

export function AppealKindSelector({
  buyOrder,
  isBuyer,
  selectAppealKind
}: {
  buyOrder: BuyOrder
  isBuyer: boolean
  selectAppealKind: (kind: AppealKindOptions) => void
}) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
  const { bottom } = useSafeAreaInsets()
  const { t } = useTranslation()

  const options: AppealKindOptions[] = useMemo(() => getAppealKindOptions(buyOrder, isBuyer), [buyOrder, isBuyer])

  return (
    <>
      <View className={'flex-1 bg-background-secondary'}>
        <ScrollView className={'flex-1 bg-background-secondary'} contentContainerStyle={{ paddingBottom: bottom + 72 }}>
          <SectionHeader>{t(`orderAppeal:selectKind.title`)}</SectionHeader>
          {options.map((kind, index) => {
            const kindStr = kind === 'AmountMismatch' ? kind : AppealKind[kind]
            return (
              <RadioListItem
                hasBorderTop={index === 0}
                hasBorderBottom={index === options.length - 1}
                label={t(`orderAppeal:selectKind.options.${kindStr}`)}
                key={index}
                onPress={() => setSelectedIdx(index)}
                isChecked={selectedIdx === index}
              />
            )
          })}
        </ScrollView>
      </View>
      <BottomActionPanel>
        <Button
          disabled={selectedIdx === null}
          size={'md'}
          className={'flex-1'}
          onPress={() => selectedIdx !== null && selectAppealKind(options[selectedIdx])}
        >
          {t(`orderAppeal:submitButton.selectKind`)}
        </Button>
      </BottomActionPanel>
    </>
  )
}

function getAppealKindOptions(buyOrder: BuyOrder, isBuyer: boolean): AppealKindOptions[] {
  const options: AppealKindOptions[] = []

  if (isBuyer) {
    switch (buyOrder.status) {
      case BuyOrderStatus.paired: {
        const { bank, alipay, wechat, /*ermb,*/ qq } = buyOrder.sellOrderPaymentMethod

        options.push(AppealKind.UploadPaymentProofFailed)

        bank && options.push(AppealKind.BankInfoError)
        alipay && options.push(AppealKind.AlipayInfoError)
        wechat && options.push(AppealKind.WechatInfoError)
        // TODO: Are we missing an option for ERMB?
        // ermb && options.push(AppealKind.)
        qq && options.push(AppealKind.QQInfoError)
        break
      }
      case BuyOrderStatus.paymentMade:
        options.push(AppealKind.SellerDidNotConfirm)
        break
      default:
        break
    }
  } else {
    // Sell orders
    options.push(AppealKind.CannotConfirmBuyerPaymentProof)
    if (buyOrder.status === BuyOrderStatus.paymentMade) {
      if (buyOrder.buyer !== '0') {
        options.push('AmountMismatch')
      }
      options.push(AppealKind.SellerNotReceivedPayment)
    }
  }
  options.push(AppealKind.Other)
  return options
}
